import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'
import { router } from '@/router'
import { decodeJwt } from '@/utils/jwt'

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  success: boolean
  data: {
    accessToken: string
    refreshToken: string
    tokenType: string
    username: string
    name: string
    role: string
    expiresIn: number
  }
  message: string
}

export interface User {
  username: string
  name: string
  role: string
  userTokenId: string
  email?: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'ADMIN')

  const login = async (credentials: LoginRequest) => {
    try {
      isLoading.value = true
      const response = await apiService.post<LoginResponse>('/api/v1/auth/login', credentials)

      if (response.success) {
        token.value = response.data.accessToken
        refreshToken.value = response.data.refreshToken

        // JWT에서 userTokenId 추출
        const jwtPayload = decodeJwt(response.data.accessToken)

        user.value = {
          username: response.data.username,
          name: response.data.name,
          role: response.data.role,
          userTokenId: jwtPayload?.userTokenId || '',
          email: credentials.username // username을 email로 사용 (실제로는 백엔드에서 받아와야 함)
        }

        localStorage.setItem('token', response.data.accessToken)
        localStorage.setItem('refreshToken', response.data.refreshToken)
        localStorage.setItem('user', JSON.stringify(user.value))
        
        return response
      }
      
      throw new Error(response.message || 'Login failed')
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    // 서버에 로그아웃 요청 (선택사항)
    try {
      await apiService.post('/api/v1/auth/logout')
    } catch (error) {
      console.warn('Server logout failed:', error)
    }
    
    // 현재 사용자 역할 저장
    const currentUserRole = user.value?.role
    
    // 클라이언트 상태 초기화
    token.value = null
    refreshToken.value = null
    user.value = null
    
    // 로컬 스토리지 정리
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    
    // 역할에 따른 리다이렉트
    if (currentUserRole === 'ADMIN') {
      // 관리자는 로그인 페이지로
      router.push('/manage/login')
    } else {
      // 일반 사용자는 기본 페이지로
      router.push('/')
    }
  }

  const initializeAuth = () => {
    const savedUser = localStorage.getItem('user')
    if (savedUser) {
      try {
        user.value = JSON.parse(savedUser)
      } catch (error) {
        console.error('Failed to parse saved user data:', error)
        logout()
      }
    }
  }

  return {
    token,
    refreshToken,
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    initializeAuth
  }
})